import { beacon, tap, opened1 } from "../../lib/beacon";
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import "./board.css";
import { BoardCard } from "./Card";
import { CardPanel, TeamOnlyPanel } from "./Panel";
import { Tiles, Yours, Filters, TopicSection, RowsSection, Bookshelf, Team, FooterFold } from "./Sections";
import { Reviews } from "./Reviews";
import { cardMap, linkOf, firstDate } from "./model";
import { effState } from "./Card";
import { PdfDrawer } from "../map";
import { PrivateTabs, PRIVATE_FRAMES } from "./PrivateFrame";
import { fmtDay, greetingWord } from "../../lib/vikas";

// salutation by India time (before 12 morning, 12–16:59 afternoon, from 17 evening); the rest is the data's words
const greetNow = (g) => `Good ${greetingWord()}. ${g.replace(/^\s*good (morning|afternoon|evening)[.,!]?\s*/i, "")}`;
const queueLine = (n) => `Good ${greetingWord()}. ${n} ${n === 1 ? "card" : "cards"} in your queue.`;
const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const isoDue = (c) => (typeof c?.due === "string" && /^\d{4}-\d{2}-\d{2}/.test(c.due) ? c.due.slice(0, 10) : "");

const EMPTY = { mine: false, owner: "", topic: "", status: "", q: "", date: "", from: "", to: "", tile: "" };

function dateWindow(f, asOf) {
  const end = asOf ? new Date(asOf + "T00:00:00Z") : new Date();
  const mk = (d) => d.toISOString().slice(0, 10);
  const back = (n) => { const s = new Date(end); s.setUTCDate(s.getUTCDate() - n); return mk(s); };
  if (f.date === "today") return [mk(end), mk(end)];
  if (f.date === "7") return [back(7), mk(end)];
  if (f.date === "30") return [back(30), mk(end)];
  if (f.date === "custom" && (f.from || f.to)) return [f.from || "0000", f.to || "9999"];
  return null;
}

function SystemHealth({ data, cards, view }) {
  const rows = data?.panels?.["22_system_health"]?.rows;
  const list = (Array.isArray(rows) ? rows : []).map((r) => cards[r.card_id] || cards[r.id] || r).filter((c) => c && c.id && c.title);
  return (
    <section className="sec" data-testid="system-health">
      <div className="sec-h"><h2>System health</h2></div>
      {list.length === 0 ? <p className="faint" data-testid="system-health-empty">Nothing in system health yet</p>
        : <div className="grid">{list.map((c) => view({ ...c, question: undefined, options: undefined }))}</div>}
    </section>
  );
}

class Guard extends React.Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch() { beacon("error", this.props.section, { reason: "render" }); }
  render() { return this.state.bad ? null : this.props.children; }
}

export function Board({ data, ctx, route, nav }) {
  const board = data.board;
  const cards = useMemo(() => cardMap(board), [board]);
  const [f, setF] = useState(EMPTY);
  const foldKey = `vikas_fold_${ctx.role}`;
  const [folded, setFolded] = useState(() => { try { return JSON.parse(localStorage.getItem(foldKey) || "[]"); } catch { return []; } });
  const saveFolds = (next) => { localStorage.setItem(foldKey, JSON.stringify(next)); setFolded(next); };
  const allFoldIds = () => (board.sections || []).filter((s) => !s.private).flatMap((s) => (
    s.kind === "topic" ? [`T:${s.key}`, ...(s.subgroups || []).map((g) => `G:${s.key}:${g.key}`), `G:${s.key}:__other`]
      : s.kind === "bookshelf" ? (s.shelves || []).map((sh) => `B:${sh.key}`) : []));
  const folds = {
    isShut: (id) => folded.includes(id),
    toggle: (id) => saveFolds(folded.includes(id) ? folded.filter((k) => k !== id) : [...folded, id]),
  };
  const foldAll = () => saveFolds(allFoldIds());
  const openAll = () => saveFolds([]);
  const [farDown, setFarDown] = useState(false);
  useEffect(() => {
    const on = () => setFarDown(window.scrollY > window.innerHeight);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const meta = data.meta || {};
  const secs = (board.sections || []).filter((s) => !s.private || PRIVATE_FRAMES.has(s.key));
  const topics = secs.filter((s) => s.kind === "topic" && !s.private);
  const lastTopic = (topics[topics.length - 1] || {}).key;
  const privOn = !f.topic || PRIVATE_FRAMES.has(f.topic);
  const people = (meta.people || []).map((p) => (typeof p === "string" ? p : p.name)).filter(Boolean);
  const tileIds = f.tile ? new Set(((board.tiles || []).find((t) => t.key === f.tile) || {}).ids || []) : null;
  const win = dateWindow(f, meta.as_of);
  const filtering = Object.keys(EMPTY).some((k) => f[k] && k !== "from" && k !== "to");
  const pressOf = (id) => ctx.pressFor(id);

  const show = (c) => {
    if (c.weight === "housekeeping") return false;
    if (tileIds && !tileIds.has(c.id)) return false;
    if (f.mine && !mineSet.has(c.id)) return false;
    if (f.owner && c.owner !== f.owner) return false;
    if (f.topic && c.topic !== f.topic) return false;
    if (f.status && effState(c, pressOf(c.id))[0] !== f.status) return false;
    if (win) { const d = firstDate(c); if (d && (d < win[0] || d > win[1])) return false; }
    if (f.q.trim()) {
      const hay = [c.id, c.title, c.context, c.next, c.owner, c.topic, c.subgroup, c.what, c.stands, ...(c.files || []).map((x) => x.name)].join(" ").toLowerCase();
      if (!hay.includes(f.q.trim().toLowerCase())) return false;
    }
    return true;
  };

  const m = route.match(/^\/card\/(.+)$/);
  const openId = m ? decodeURIComponent(m[1]) : null;
  const openCard = openId ? cards[openId] : null;
  const opener = useRef(null);
  const open = (c) => {
    tap(c.topic || (c.team_only ? "team" : "board"), "open_card", c.id);
    const a = document.activeElement;
    opener.current = a && a !== document.body ? a : document.querySelector(`[data-testid="card-${c.id}"]`);
    nav(linkOf(c));
  };
  const [backTo, setBackTo] = useState(null);
  const openRow = (g, id) => { setBackTo(g.id); nav(`/card/${encodeURIComponent(id)}`); };
  const close = useCallback(() => {
    setBackTo(null);
    nav("/");
    const el = opener.current;
    if (el) requestAnimationFrame(() => el.isConnected && el.focus({ preventScroll: true }));
  }, [nav]);
  useEffect(() => {
    if (!openId) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    const onDown = (e) => {
      if (window.innerWidth <= 760) return;
      if (e.target.closest?.('[data-testid="side-column"], [data-opens-card], [data-testid="pdf-drawer"], [role="dialog"], .totop, .vbubble')) return;
      close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [openId, close]);
  const pdf = (c) => c.file && ctx.openDrawer(<PdfDrawer file={c.file} title={c.title} />);
  useEffect(() => { document.title = board.name || "VIKAS Dashboard"; }, [board.name]);
  useEffect(() => {
    if (!openId || window.innerWidth > 760) return undefined;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [openId]);

  const isReviews = route === "/eshan";
  const isHealth = route === "/health" && ctx.userAdmin;
  const queue = meta.queues && meta.queues[ctx.role] && Array.isArray(meta.queues[ctx.role].ids) ? meta.queues[ctx.role] : null;
  const yoursIds = queue
    ? queue.ids.filter((id) => { const d = isoDue(cards[id]); return d && d <= todayIST() && cards[id]?.weight !== "housekeeping"; })
    : meta.yours && Object.prototype.hasOwnProperty.call(meta.yours, ctx.role) ? meta.yours[ctx.role] || [] : null;
  const needSet = useMemo(() => Object.assign(new Set(yoursIds || []), { quiet: Boolean(queue) }), [yoursIds, queue]);
  const mineList = (meta.mine && meta.mine[ctx.role]) || [];
  const mineSet = useMemo(() => new Set(mineList), [mineList]);
  const setFB = (nf) => { tap(nf.mine ? "my_cards" : nf.topic || "filters", "filter"); setF(nf); };
  const mineTeam = mineList.map((id) => cards[id]).filter((c) => c && c.team_only);
  useEffect(() => {
    if (!f.mine) return;
    opened1("my_cards");
    if (mineList.length && !mineList.some((id) => cards[id])) beacon("blank", "my_cards", { reason: "mine_empty" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.mine]);
  useEffect(() => { if (openId && !openCard) beacon("blank", "board", { reason: "no_data", card_id: openId }); }, [openId, openCard]);
  const view = (c) => <BoardCard key={c.id} c={c} press={pressOf(c.id)} sel={c.id === openId} onOpen={open} ctx={ctx} />;
  useEffect(() => {
    document.body.classList.toggle("vb-panel-open", Boolean(openId));
    return () => document.body.classList.remove("vb-panel-open");
  }, [openId]);
  const closedRows = (s) => {
    const base = (s.card_ids || []).map((id) => cards[id]).filter(Boolean);
    if (!f.q.trim()) return base.filter(show);
    const older = Object.values(cards).filter((c) => c.state === "closed" && !base.includes(c));
    return [...base, ...older].filter(show);
  };

  const node = (s) => {
    switch (s.kind) {
      case "topic":
        if (s.private) return null;
        if (f.topic && s.key !== f.topic) return null;
        return <TopicSection key={s.key} s={s} cards={cards} show={(c) => !c.team_only && show(c)} filtering={filtering} view={view} folds={folds} need={needSet} />;
      case "bookshelf": return <Bookshelf key={s.key} s={s} cards={cards} show={show} onOpen={open} onPdf={pdf} folds={folds} filtering={filtering} />;
      case "team": return <Team key={s.key} s={s} owner={f.owner} setOwner={(o) => setFB({ ...f, owner: o, mine: false })} cards={cards} view={view} />;
      case "not_placed":
        return <RowsSection key={s.key} s={s} title="Not yet placed" filtering={filtering} onOpen={open} pressOf={pressOf}
          rows={(s.card_ids || []).map((id) => cards[id]).filter((c) => c && !c.team_only && show(c))} right={(c) => c.topic || ""} />;
      case "closed":
        return <RowsSection key={s.key} s={s} filtering={filtering} onOpen={open} pressOf={pressOf} limit={f.q.trim() ? 0 : 20}
          rows={closedRows(s)} right={(c) => (c.closed_on ? `Closed ${fmtDay(c.closed_on)}` : "")} />;
      case "footer": return <FooterFold key={s.key} s={s} data={data} ctx={ctx} />;
      default: return null;
    }
  };

  return (
    <div className="vshell">
      <div className="vmain">
        {isReviews ? <Reviews data={data} ctx={ctx} nav={nav} /> : isHealth ? <SystemHealth data={data} cards={cards} view={view} /> : (
          <>
            {queue ? <div className="greet" data-testid="greeting">{queueLine(Number(queue.count) || 0)}</div>
              : board.greeting && <div className="greet" data-testid="greeting">{greetNow(board.greeting)}</div>}
            <Tiles tiles={board.tiles} usage={meta.usage} tile={f.tile} setTile={(t) => setFB({ ...f, tile: t })} />
            <Yours ids={yoursIds} cards={cards} onOpen={open} pressOf={pressOf} onAll={() => setFB({ ...f, mine: true, owner: "" })}
              onTopic={queue ? null : (t) => setFB({ ...f, topic: f.topic === t ? "" : t })} />
            <Filters f={f} setF={setFB} people={people} topics={topics} privates={[...PRIVATE_FRAMES]} onFoldAll={foldAll} onOpenAll={openAll} />
            {f.mine && mineSet.size === 0 && (
              <p className="mine-none" data-testid="mine-none">None of your cards is on the board. Your private asks are in the private topics.</p>
            )}
            {f.mine && mineTeam.filter(show).length > 0 && (
              <section className="sec" data-testid="mine-team"><div className="grid">{mineTeam.filter(show).map(view)}</div></section>
            )}
            {secs.map((s) => (
              <React.Fragment key={`n-${s.key}`}>
                <Guard section={s.key}>{node(s)}</Guard>
                {s.key === lastTopic && privOn && <PrivateTabs theme={ctx.theme} act={ctx.act} want={PRIVATE_FRAMES.has(f.topic) ? f.topic : ""} />}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
      {farDown && (
        <button className="totop" data-testid="back-to-top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>↑ Back to top</button>
      )}
      {openId && !isReviews && !isHealth && (
        <div className="side" data-testid="side-column">
          {openCard && openCard.team_only ? <TeamOnlyPanel key={openCard.id} c={openCard} press={pressOf(openCard.id)} ctx={ctx} onClose={close} />
            : openCard ? <CardPanel key={openCard.id} c={openCard} press={pressOf(openCard.id)} ctx={ctx} onClose={close} onPdf={pdf}
              onRow={(id) => openRow(openCard, id)} back={backTo && backTo !== openCard.id && cards[backTo] ? () => { setBackTo(null); nav(`/card/${encodeURIComponent(backTo)}`); } : null} />
            : <aside className="panel" data-testid="card-panel"><div className="ph"><button className="x" aria-label="Close the panel" data-testid="panel-close" onClick={close}>×</button><h2>No card {openId} on the board</h2></div></aside>}
        </div>
      )}
    </div>
  );
}
